import _ from 'lodash'
import filesize from 'filesize'
import KeyValuePair from '@/modules/core/support/KeyValuePair'
import User from '@/modules/user/model/User'

/**
 * Indica el estado de la entidad durante el tiempo de ejecución de
 * aplicación
 */
export enum EntityState {
    Clean = 'clean',
    Modified = 'modified',
    Updated = 'updated',
}

/**
 * Objeto multimedia
 */
export class MediaObject {
    public id = 0
    public key = ''
    public title = ''
    public alt = ''
    public caption = ''
    public path = ''
    public file: File
    public state: EntityState = EntityState.Clean
    public thumbnails: {[size: string]: string} = {}
    public url = ''
    public mimeType = ''
    public creator = new User()
    public createdAt: Date = new Date()
    public updateAt: Date = new Date()
    public originalName = ''
    public size = 0
    public tags: string[] = []
    public mediaFound = true

    public get isNew () {
        return !this.id
    }

    public static convert (data: {[key: string]: any}) {
        const media = new MediaObject()
        media.id = data.id
        media.key = data.key
        media.title = data.title
        media.alt = data.alt
        media.caption = data.caption
        media.path = data.path
        media.thumbnails = data.thumbnails || {}
        media.url = data.url || ''
        media.mimeType = data.mime_type
        media.createdAt = new Date(data.created_at)
        media.updateAt = new Date(data.updated_at)
        media.originalName = data.original_name
        media.size = data.size
        media.tags = data.tags || []
        media.mediaFound = data.media_found || false
        if (data.creator) {
            media.creator = User.deserialize(data.creator)
        }

        return media
    }

    /**
     * Indica si el archivo del objeto multimedia es una imagen
     */
    public get isImage (): boolean {
        return this.mimeType.split('/')[0] === 'image'
    }

    /**
     * Indica si el archivo del objeto multimedia es un pdf
     */
    public get isPDF (): boolean {
        return  this.mimeType === 'application/pdf'
    }

    public get isVideo (): boolean {
        return this.mimeType.split('/')[0] === 'video'
    }

    public thumbnail (size: string): string {
        return this.thumbnails[size] || ''
    }

    public serialize (): KeyValuePair {
        return {
            key: this.key,
            title: this.title,
            caption: this.caption,
            alt: this.alt,
            original_name: this.originalName,
            tags: this.tags,
        }
    }

    /**
     * Indica si el objeto multimedia tiene contenido
     */
    get hasContent (): boolean {
        return _.some(_.pick(this, ['key', 'caption', 'title', 'alt', 'originalName']), v => !_.isEmpty(v))
    }

    /**
     * Compara el contenido de dos objetos multimedia y determinada si es idéntico
     *
     * @param a
     * @param b
     */
    public static contentIsEqual (a: MediaObject, b: MediaObject): boolean {
        const props = ['key', 'title', 'caption', 'alt', 'tags']

        return _.isEqual(
            _.pick(a, props),
            _.pick(b, props),
        )
    }

    public assign (media: MediaObject): MediaObject {
        _.assign(this, _.pickBy(media, _.identity))

        return this
    }

    public get formattedSize (): string {
        if (!this.size) {
            return '0 bytes'
        }

        return filesize(this.size, {exponent: 2})
    }
}

/**
 * Estado de la cola
 *
 * Define los posibles estados de la cola de archivos que serán subidos
 * al servidor.
 *
 * @author Alejandro G. Riaño <agutierrez@softcontrol.es>
 */
export enum QueueState {
    /**
     * La cola se encuentra abierta y acepta nuevos ficheros para ser
     * subidos al servidor.
     */
    Open = 'QUEUE_OPEN',

    /**
     * La cola se esta procesando.
     *
     * Los ficheros de la cola están siendo subidos al servidor. La aplicación
     * no acepta añadir nuevos ficheros a la cola en proceso.
     */
    Processing = 'QUEUE_PROCESSING',

    /**
     * Cola finalizada
     *
     * La aplicación ha finalizado el procesamiento de la cola. Todos los ficheros
     * ha sido subidos al servidor y este los ha aceptado o rechazado.
     * La cola se encuentra cerrada y no permite añadir nuevos
     * ficheros. Añadir un nuevo fichero resultará en la
     * creación de una nuevo cola limpia.
     */
    Terminated = 'QUEUE_TERMINATED',
}

/**
 * Estados del archivo de subida
 *
 * @author Alejandro G. Riaño <agutierrez@softcontrol.es>
 */
export enum MediaUploadState {
    /**
     * En cola
     *
     * El archivo se encuentra en la cola de subida esperando a ser enviado
     * al servidor.
     */
    Queued,

    /**
     * Optimizando
     *
     * Si el archivo es una imagen el cliente puede realizar tareas de optimización
     * y compresión para aliviar la carga del servidor.
     */
    Optimizing,

    /**
     * Enviando
     *
     * EL proceso de subida del archivo al servidor ha comenzado y la aplicación se
     * encuentra enviando el archivo al servidor.
     */
    Transferring,

    /**
     * Esperando
     *
     * La aplicación ha terminado de enviar el archivo y esta esperando la respuesta del
     * servidor.
     */
    WaitingResponse,

    /**
     * El servidor ha aceptado el archivo de subida y el proceso ha finalizado
     * correctamente
     */
    Success,

    /**
     * El proceso de subida ha fallado o el servidor ha rechazado el archivo
     */
    Failed,
}

export enum ThumbnailSize {
    Small = 'small',
    Medium = 'medium',
    Large = 'large',
}

/**
 * Representa la subida de un fichero
 *
 * @author Alejandro G. Riaño <agutierrez@softcontrol.es>
 */
export class MediaUpload {
    public progress = 0
    public state: MediaUploadState = MediaUploadState.Queued
    public readonly id = _.uniqueId('media_upload')
    public readonly thumbnails = new Map<ThumbnailSize, Blob>()
    public optimizedFile: Blob | null = null

    public thumbnail = ''

    constructor (public file: File | null = null, public media = new MediaObject()) {
        if (this.file) {
            this.media.originalName = this.file.name
        }
    }

    /**
     * Muestra el tamaño del fichero en MB
     */
    get filesize (): string {
        const file = this.file
        if (!file) {
            return ''
        }

        return filesize(file.size, { exponent: 2 })
    }

    get optimizedFileSize (): string {
        if (!this.optimizedFile) {
            return ''
        }

        return filesize(this.optimizedFile.size, {exponent: 1})
    }

    get isProcessing (): boolean {
        return this.state > MediaUploadState.Queued
            && this.state < MediaUploadState.Success
    }

    get queued () {
        return this.state === MediaUploadState.Queued
    }

    get isSuccess (): boolean {
        return this.state === MediaUploadState.Success
    }

    /**
     * Indica si la subida del fichero ha sido fallida
     */
    public get failed () {
        return this.state === MediaUploadState.Failed
    }

    /**
     * Tipo mime del archivo de la descarga
     */
    public get type () {
        return this.file ? this.file.type : ''
    }

    /**
     * Indica si el archivo de la subida es una imagen
     */
    public get isImage (): boolean {
        return this.type.split('/')[0] === 'image'
    }

    /**
     * Indica si el archivo de la descarga es un PDF
     */
    public get isPDF () {
        return this.type === 'application/pdf'
    }

    /**
     * Indica si el archivo de la descarga es un video
     */
    public get isVideo (): boolean {
        return this.type.split('/')[0] === 'video'
    }
}

export interface MediaDeletionResult {
    success: boolean
    removed: number[]
}

export class UploadConstrains {
    /**
     * Indica los tipos MIME permitidos por la aplicación para subir
     * ficheros.
     *
     * @return { string[] }
     */
    public allowedTypes: string[] = []

    /**
     * Indica en bytes el tamaño máximo permitidos de los ficheros que sean
     * subidos a la aplicación.
     */
    public maxUploadSize: number = -1;

    /**
     * Tamaños para las miniaturas y su respectivo porcentaje de re-dimensión
     */
    public thumbnailSizes: {[key: string]: number}

    /**
     * Crea una entidad a partir de los datos obtenidos de la API
     *
     * @param data
     */
    public static deserialize (data: KeyValuePair) {
        return Object.assign(new this(), {
            allowedTypes: data.allowed_types || [],
            maxUploadSize: data.max_upload_size || -1,
            thumbnailSizes: data.thumbnail_sizes || {},
        })
    }
}
